import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, Indicator, SCROLL_INDICATOR_INSETS, Spacer, SquareBadge, CurrencyAmountText, rem, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import StatusIcon from 'design-system/StatusIcon';
import ListEmptyView from 'design-system/ListEmptyView';
import Text from 'design-system/Text';
import { Avatar } from 'design-system/avatar';
import ProgressCard from 'features/invest/components/InvestOnboardingFunnel/ProgressCard';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { formatAmount, formatDateForItem } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { GET_CREDIT_REFERRALS_SUCCESS } from 'actions/types';
import { isWeb } from '../../../constants';
import { getCreditReferrals, setReferralSeen } from '../actions';
import useInviteFriends from '../hooks/useInviteFriends';
const CreditReferralDetailsScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const id = route.params?.id;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => (<NavigationCloseButton onPress={() => {
                    navigation.pop();
                }}/>),
            headerBackVisible: false,
        });
    }, []);
    const data = useAppSelector((store) => store.referrals.creditReferrals?.referrals.find((item) => item.id === id));
    const dispatch = useAppDispatch();
    // Opening a /referral link on web, or reloading it, can land here before
    // the referrals have been fetched, or with a persisted list that predates
    // this referral, so fetch them whenever the referral isn't in the store.
    const [fetchStatus, setFetchStatus] = useState(data ? 'done' : 'fetching');
    // The id can change while a request is in flight, so only the latest
    // request sets the status.
    const latestFetchRef = useRef(0);
    const fetchReferrals = useCallback(() => {
        latestFetchRef.current += 1;
        const requestNumber = latestFetchRef.current;
        const setStatusIfLatest = (status) => {
            if (requestNumber === latestFetchRef.current)
                setFetchStatus(status);
        };
        setFetchStatus('fetching');
        Promise.resolve(dispatch(getCreditReferrals()))
            .then((result) => {
            setStatusIfLatest(result?.type === GET_CREDIT_REFERRALS_SUCCESS ? 'done' : 'failed');
        })
            .catch(() => setStatusIfLatest('failed'));
    }, [dispatch]);
    // On web this screen stays mounted when the browser moves between
    // /referral links, so this runs again for each id.
    useEffect(() => {
        if (!data)
            fetchReferrals();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- data is left out so a fetch with no match shows "Referral not found" instead of fetching again
    }, [id, fetchReferrals]);
    useEffect(() => {
        if (data?.status === 'COMPLETED' && !data.seenAt) {
            dispatch(setReferralSeen({
                ids: [data.id],
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.status]);
    const onPressInvite = useInviteFriends();
    const onPressClose = useCallback(() => {
        navigation.pop();
    }, []);
    const amount = formatAmount(data?.getCreditAmount.value, data?.getCreditAmount.currency);
    const colors = useColors();
    const styles = useStyles(styleSheet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT * (data?.status === 'COMPLETED' ? 2 : 1), paddingHorizontalStyle);
    if (!data) {
        return (<AppView>
            {fetchStatus === 'fetching' && <Indicator black/>}
            {fetchStatus === 'failed' && (<ListEmptyView onCard title="Couldn’t load this referral" subTitle="Check your connection and try again.">
                <Button title="Try again" small hug brandReversed onPress={fetchReferrals}/>
              </ListEmptyView>)}
            {fetchStatus === 'done' && (<ListEmptyView onCard title="Referral not found" subTitle="We couldn’t find this referral. You can see all your invites in Track invites."/>)}
          </AppView>);
    }
    return (<AppView type="primary" withFrame={false}>
          <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle}>
            <View style={styles.headerContainer}>
              <View style={styles.avatarContainer}>
                <View>
                  <Avatar name={data.firstName} lastName={data.lastName} size={44} color={colors.background.brandLight} fontColor={colors.text.brand} primary borderWidth={1} style={styles.avatar}/>
                </View>
                <SquareBadge border fill={colors.background.light}>
                  <StatusIcon state={data.status === 'COMPLETED' ? 'done' : 'in-progress'}/>
                </SquareBadge>
              </View>
              <Text Text-14 Secondary centered>
                {data.status === 'COMPLETED'
            ? 'You received credits!'
            : 'Referral pending'}
              </Text>
              <Spacer h={8}/>
              <CurrencyAmountText size="XL" value={{
            amount: data.getCreditAmount.value,
            currency: data.getCreditAmount.currency,
        }} showFraction={false}/>
              <Spacer h={16}/>
            </View>
            <ProgressCard completed noTextDecoration alwaysShowChildren alwaysShowTimeline timeInfo={data.signedUpAt ? formatDateForItem(data.signedUpAt) : undefined} title={`${data.firstName} created an account`}>
              <Text TextThin-14 Secondary>
                Your friend has signed up to Emma via your referral link
              </Text>
            </ProgressCard>
            <ProgressCard isActiveStep completed={data.status === 'COMPLETED'} noTextDecoration alwaysShowChildren alwaysShowTimeline lastItem timeInfo={data.completedAt
            ? formatDateForItem(data.completedAt)
            : undefined} title={data.status === 'COMPLETED'
            ? `${data.firstName} has started the trial`
            : `${data.firstName} has not yet started the trial`}>
              <Text TextThin-14 Secondary>
                {data.status === 'COMPLETED' ? (<>
                    {amount} worth of subscription credits have been added to
                    your account because {data.firstName} has started a{' '}
                    {data.giveFreeTrialDays}
                    -day trial!
                  </>) : (<>
                    Tell your friend to start an extended{' '}
                    {data.giveFreeTrialDays}
                    -day trial on any of our plans to gain your {amount} in
                    credits
                  </>)}
              </Text>
            </ProgressCard>
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            {data.status === 'COMPLETED' && (<Button title="Invite more friends" brandReversed onPress={onPressInvite}/>)}
            <Button title="Close" brand onPress={onPressClose}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.light,
        colors.cards.onLight,
    ],
}));
const styleSheet = createStyleSheets((colors) => ({
    avatar: {
        borderColor: colors.borders.iconStroke,
    },
    headerContainer: {
        alignItems: 'center',
    },
    avatarContainer: {
        marginBottom: rem(18),
    },
}));
export default CreditReferralDetailsScreen;
